// note: general import
import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  getProfile,
  updateProfile,
  updatePictureProfile,
  getUser,
  updateUserRoles,
  getUserByRole,
  createUser,
  deleteUser,
  updateUser,
} from "libs/api/user";

// note: get profile thunk
export const getProfileThunk = createAsyncThunk(
  "profile/get",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, pathname }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await getProfile(pathname);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response?.data?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw JSON.stringify({ message: error.message });
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// note: update profile thunk
export const updateProfileThunk = createAsyncThunk(
  "profile/update",
  async ({
    cbPending,
    cbFulfilled,
    cbRejected,
    cbFinally,
    pathname,
    body,
    accessToken,
  }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await updateProfile(pathname, body, accessToken);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw error;
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// note: update picture thunk
export const updatePictureProfileThunk = createAsyncThunk(
  "profile/picture/update",
  async ({
    cbPending,
    cbFulfilled,
    cbRejected,
    cbFinally,
    pathname,
    body,
    accessToken,
  }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await updatePictureProfile({
        pathname,
        body,
        accessToken,
      });
      typeof cbFulfilled === "function" && cbFulfilled();
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw JSON.stringify({ message: error.message });
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// note: get user thunk
export const getUserThunk = createAsyncThunk(
  "user/get",
  async ({
    cbPending,
    cbFulfilled,
    cbRejected,
    cbFinally,
    pathname,
    queryParams,
    headers,
  }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await getUser(pathname, queryParams, headers);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw error;
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// note: update user roles thunk
export const updateUserRolesThunk = createAsyncThunk(
  "update-user/role",
  async ({
    cbPending,
    cbFulfilled,
    cbRejected,
    cbFinally,
    pathname,
    queryParams,
    body,
  }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await updateUserRoles(pathname, queryParams, body);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw error;
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// note: get user thunk
export const getUserRoleThunk = createAsyncThunk(
  "user-role/get",
  async ({
    cbPending,
    cbFulfilled,
    cbRejected,
    cbFinally,
    pathname,
    accessToken,
  }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await getUserByRole(pathname, accessToken);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw error;
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// note: create user thunk
export const createUserThunk = createAsyncThunk(
  "user/create",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, pathname, body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await createUser(pathname, body);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw error;
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// note: update user thunk
export const updateUserThunk = createAsyncThunk(
  "user/update",
  async ({
    cbPending,
    cbFulfilled,
    cbRejected,
    cbFinally,
    pathname,
    body,
    headers,
  }) => {
    try {
      typeof cbPending === "function" && cbPending();

      const response = await updateUser(pathname, body, headers);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw error;
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// Delete User Thunk
export const deleteUserThunk = createAsyncThunk(
  "user/delete",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, pathname, body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await deleteUser(pathname, body);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);
